import { userApi as api, userSilentApi as silentApi } from "../../utils/apiInstances";

const asRecord = (value: unknown): Record<string, unknown> | null => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return null;
    }

    return value as Record<string, unknown>;
};

const shouldFallbackAnalyticsEndpoint = (error: unknown): boolean => {
    const message = String(error instanceof Error ? error.message : error ?? "")
        .toLowerCase()
        .trim();

    return (
        message.includes("route api/v1/organizer/analytics") ||
        message.includes("/organizer/analytics") ||
        message.includes("not found") ||
        message.includes("could not be found")
    );
};

export const handleOrganizerFetchGames = async <TData = unknown>(params: Record<string, unknown> = {}) => {
    const query = Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== null && value !== "")
        .map(
            ([key, value]) =>
                `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
        )
        .join("&");

    const endpoint = `/organizer/games${query ? `?${query}` : ""}`;

    try {
        const response = await silentApi<TData>("GET", endpoint);
        return response;
    } catch (error) {
        console.error("Failed to fetch organizer games:", error);
        throw error;
    }
};

export const handleOrganizerFetchGame = async (gameId: string | number) => {
    try {
        const response = await silentApi("GET", `/organizer/games/${gameId}`);
        return response;
    } catch (error) {
        console.error("Failed to fetch organizer game:", error);
        throw error;
    }
};

export const handleOrganizerCreateGame = async (payload: unknown) => {
    try {
        const response = await api("POST", "/organizer/games", payload);
        return response;
    } catch (error) {
        console.error("Failed to create organizer game:", error);
        throw error;
    }
};

export const handleOrganizerPreviewGame = async (payload: unknown) => {
    try {
        const previewPayload = {
            ...(typeof payload === "object" && payload !== null
                ? (payload as Record<string, unknown>)
                : {}),
            preview: true,
        };

        const response = await api("POST", "/organizer/games", previewPayload);
        return response;
    } catch (error) {
        console.error("Failed to preview organizer game:", error);
        throw error;
    }
};

export const handleOrganizerUpdateGame = async (gameId: string | number, payload: unknown) => {
    try {
        const response = await api("PATCH", `/organizer/games/${gameId}`, payload);
        return response;
    } catch (error) {
        console.error("Failed to update organizer game:", error);
        throw error;
    }
};

export const handleOrganizerDeleteGame = async (gameId: string | number) => {
    try {
        const response = await api("DELETE", `/organizer/games/${gameId}`);
        return response;
    } catch (error) {
        console.error("Failed to delete organizer game:", error);
        throw error;
    }
};

export const handleOrganizerFetchGameFinance = async <TData = unknown>(gameId: string | number) => {
    try {
        const response = await silentApi<TData>(
            "GET",
            `/organizer/games/${gameId}/finance`
        );
        return response;
    } catch (error) {
        console.error("Failed to fetch game finance summary:", error);
        throw error;
    }
};

export const handleOrganizerFetchAnalytics = async (): Promise<unknown> => {
    try {
        const response = await silentApi("GET", "/organizer/analytics");
        return response;
    } catch (error) {
        if (shouldFallbackAnalyticsEndpoint(error)) {
            try {
                const fallbackResponse = await silentApi<Record<string, unknown>>(
                    "GET",
                    "/organizer/dashboard/summary"
                );

                const fallbackRoot = asRecord(fallbackResponse);
                const fallbackData = asRecord(fallbackRoot?.data);
                const financeAnalytics = asRecord(fallbackData?.finance_analytics);

                if (fallbackRoot) {
                    return {
                        ...fallbackRoot,
                        data: financeAnalytics ?? {},
                    };
                }
            } catch (fallbackError) {
                console.error(
                    "Failed fallback organizer analytics from dashboard summary:",
                    fallbackError
                );
            }
        }

        console.error("Failed to fetch organizer analytics:", error);
        throw error;
    }
};

export const handleOrganizerFetchGamePayouts = async (gameId: string | number) => {
    try {
        const response = await silentApi(
            "GET",
            `/organizer/games/${gameId}/payouts`
        );
        return response;
    } catch (error) {
        console.error("Failed to fetch game payouts:", error);
        throw error;
    }
};

export const handleOrganizerRequestGamePayout = async (gameId: string | number, payload: unknown = {}) => {
    try {
        const basePayload: Record<string, unknown> =
            payload && typeof payload === "object" && !Array.isArray(payload)
                ? { ...(payload as Record<string, unknown>) }
                : {};

        if (!("idempotency_key" in basePayload)) {
            basePayload.idempotency_key = `web-payout-${gameId}-${Date.now()}`;
        }

        const response = await api(
            "POST",
            `/organizer/games/${gameId}/payouts`,
            basePayload
        );
        return response;
    } catch (error) {
        console.error("Failed to request game payout:", error);
        throw error;
    }
};
